import { useEffect } from 'react';

// Explicit markers are preferred. The legacy fixed/inset selector keeps older modals safe while
// they migrate, but inactive drawers/backdrops must never freeze the page.
const OVERLAY_SELECTOR = '[data-modal-overlay], .fixed.inset-0';
const SCROLL_ROOT_SELECTOR = '[data-modal-scroll-root]';
const LOCKED_PROPERTIES = ['overflow', 'overflow-x', 'overflow-y'];

const isActiveOverlay = (element) => {
  if (!(element instanceof HTMLElement) || !element.isConnected) return false;
  if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
  if (element.getAttribute('data-modal-overlay') === 'false') return false;
  if (element.getAttribute('data-state') === 'closed') return false;

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (style.pointerEvents === 'none' && Number(style.opacity) === 0) return false;
  return true;
};

const activeOverlays = () => (
  Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).filter(isActiveOverlay)
);

const belongsToOverlay = (element, overlays) => (
  overlays.some((overlay) => overlay === element || overlay.contains(element))
);

const getLockTargets = (overlays) => {
  const targets = new Set([document.documentElement, document.body]);

  document.querySelectorAll(SCROLL_ROOT_SELECTOR).forEach((element) => {
    if (!belongsToOverlay(element, overlays)) targets.add(element);
  });

  // Compatibility for existing page layouts that have not yet annotated their scroll roots.
  const main = document.querySelector('main');
  if (main && !belongsToOverlay(main, overlays)) {
    targets.add(main);
    if (main.parentElement) {
      if (!belongsToOverlay(main.parentElement, overlays)) targets.add(main.parentElement);
      // The exercise layout uses side-by-side columns (e.g. main content + Injector panel);
      // lock those sibling columns too so neither column's scrollbar paints over a modal.
      Array.from(main.parentElement.children).forEach((child) => {
        if (!belongsToOverlay(child, overlays)) targets.add(child);
      });
    }
  }
  return Array.from(targets).filter(Boolean);
};

const snapshotOverflow = (element) => (
  LOCKED_PROPERTIES.reduce((snapshot, property) => ({
    ...snapshot,
    [property]: {
      value: element.style.getPropertyValue(property),
      priority: element.style.getPropertyPriority(property),
    },
  }), {})
);

const lockOverflow = (element) => {
  element.style.setProperty('overflow', 'hidden', 'important');
};

const restoreOverflow = (element, snapshot) => {
  LOCKED_PROPERTIES.forEach((property) => {
    const saved = snapshot[property];
    if (saved?.value) {
      element.style.setProperty(property, saved.value, saved.priority);
    } else {
      element.style.removeProperty(property);
    }
  });
};

export default function ModalScrollLock() {
  useEffect(() => {
    const saved = new Map();

    const restoreAll = () => {
      saved.forEach((snapshot, element) => restoreOverflow(element, snapshot));
      saved.clear();
    };

    const apply = () => {
      const overlays = activeOverlays();
      if (!overlays.length) {
        restoreAll();
        return;
      }

      const desiredTargets = new Set(getLockTargets(overlays));

      // A scroll root can be removed, moved into an overlay, or lose its annotation while a
      // nested modal remains open. Restore it immediately rather than waiting for every modal.
      saved.forEach((snapshot, element) => {
        if (!desiredTargets.has(element)) {
          restoreOverflow(element, snapshot);
          saved.delete(element);
        }
      });

      // Discover roots added after the first modal opened and preserve their exact inline state.
      desiredTargets.forEach((element) => {
        if (!saved.has(element)) saved.set(element, snapshotOverflow(element));
        lockOverflow(element);
      });
    };

    const observer = new MutationObserver(apply);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'aria-hidden',
        'class',
        'data-modal-overlay',
        'data-modal-scroll-root',
        'data-state',
        'hidden',
      ],
    });
    apply();
    return () => {
      observer.disconnect();
      restoreAll();
    };
  }, []);

  return null;
}
